import { ANALYTICS_ENABLED } from "@/lib/config";
import { AUTH_CHANGE_EVENT, getUserId } from "@/lib/auth";
import {
  captureFirstTouchAttribution,
  getFirstTouchAttribution,
  toUtmPayload,
} from "./attribution";
import { isBrowser, newUuid } from "./browser";
import { buildEventContext, currentPagePath } from "./context";
import type {
  AnalyticsEventName,
  AnalyticsEventPayload,
  AnalyticsPropertiesFor,
} from "./events";
import { postAnalyticsJson } from "./http";
import {
  getAnonymousId,
  isAnonymousLinkActive,
  clearAnonymousId,
  markAnonymousLinked,
  clearAnonymousLink,
  readLinkedAnonymousId,
} from "./identity";
import { getOrCreateSession, rotateSession } from "./session";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let lastPageViewUrl: string | null = null;
let lastPageViewAt = 0;
let authListenerBound = false;
let lastObservedUserId: string | null | undefined;

function resolveUserId(): string | undefined {
  try {
    const userId = getUserId();
    if (!userId) return undefined;
    return UUID_RE.test(userId) ? userId : undefined;
  } catch {
    return undefined;
  }
}

function currentUserId(): string | null {
  return resolveUserId() ?? null;
}

function shouldSkipDuplicatePageView(pageUrl: string | null): boolean {
  if (!pageUrl) return false;
  const now = Date.now();
  if (lastPageViewUrl === pageUrl && now - lastPageViewAt < 1000) {
    return true;
  }
  lastPageViewUrl = pageUrl;
  lastPageViewAt = now;
  return false;
}

export function buildAnalyticsEvent<E extends AnalyticsEventName>(
  event: E,
  properties?: AnalyticsPropertiesFor<E>,
  eventId = newUuid(),
): AnalyticsEventPayload<E> {
  captureFirstTouchAttribution();
  const userId = resolveUserId();
  // After linking, keep sending the already-linked browser id: ingestion rejects an
  // event whose userId it cannot back with a verified token unless anonymousId is present,
  // and re-sending the same anonymousId/userId pair is an idempotent link.
  const anonymousId =
    userId && isAnonymousLinkActive()
      ? readLinkedAnonymousId() ?? undefined
      : getAnonymousId().anonymousId || undefined;
  const { sessionId } = getOrCreateSession();
  const attribution = getFirstTouchAttribution();
  const context = buildEventContext();

  const payload: AnalyticsEventPayload<E> = {
    event,
    eventId,
    sessionId,
    timestamp: new Date().toISOString(),
    utm: toUtmPayload(attribution),
    context,
    properties: (properties ?? {}) as AnalyticsPropertiesFor<E>,
  };

  if (anonymousId) {
    payload.anonymousId = anonymousId;
  }
  if (userId) {
    payload.userId = userId;
  }

  return payload;
}

export function sendAnalyticsEvent(
  payload: AnalyticsEventPayload,
): Promise<boolean> {
  return postAnalyticsJson("/v1/analytics/events", payload).then((accepted) => {
    if (accepted && payload.userId && payload.anonymousId) {
      clearAnonymousId();
      markAnonymousLinked(payload.anonymousId);
    }
    return accepted;
  });
}

function dispatch(payload: AnalyticsEventPayload): void {
  void sendAnalyticsEvent(payload);
}

function emitLifecycleIfNeeded(): void {
  if (resolveUserId() && isAnonymousLinkActive()) {
    getOrCreateSession();
    return;
  }
  const anon = getAnonymousId();
  getOrCreateSession();
  if (anon.created) {
    dispatch(buildAnalyticsEvent("visitor"));
  }
}

function syncSessionForAuthChange(): void {
  const current = currentUserId();
  if (lastObservedUserId === undefined) {
    lastObservedUserId = current;
    return;
  }
  if (current === lastObservedUserId) {
    return;
  }

  const previous = lastObservedUserId;
  lastObservedUserId = current;

  // First login from an anonymous visit keeps the active analytics session.
  if (previous === null && current !== null) {
    return;
  }

  // Logout or a different authenticated user: start a new analytics session once.
  clearAnonymousLink();
  rotateSession();
}

export function track<E extends AnalyticsEventName>(
  event: E,
  properties?: AnalyticsPropertiesFor<E>,
): void {
  try {
    if (!ANALYTICS_ENABLED || !isBrowser()) return;
    emitLifecycleIfNeeded();
    if (event === "page_view") {
      if (shouldSkipDuplicatePageView(currentPagePath())) return;
    }
    dispatch(buildAnalyticsEvent(event, properties));
  } catch {
    // Analytics must never break product flows.
  }
}

function onAuthChange(): void {
  try {
    syncSessionForAuthChange();
  } catch {
    // ignore
  }
}

export function initAnalytics(): void {
  try {
    if (!ANALYTICS_ENABLED || !isBrowser()) return;
    captureFirstTouchAttribution();
    emitLifecycleIfNeeded();
    syncSessionForAuthChange();
    if (!authListenerBound) {
      window.addEventListener(AUTH_CHANGE_EVENT, onAuthChange);
      authListenerBound = true;
    }
  } catch {
    // ignore
  }
}

export const analytics = {
  track,
  init: initAnalytics,
};

export function resetAnalyticsClientForTests(): void {
  lastPageViewUrl = null;
  lastPageViewAt = 0;
  lastObservedUserId = undefined;
  authListenerBound = false;
}
